"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Selections.scss";
import { fetchSelections, fetchTracksBySelection, Selections, Track } from "../../services/api";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import TrackItem from "../../components/TrackItem/TrackItem";
import Loader from "../../components/Loader/Loader";
import CloseButton from "../../components/CloseButton/CloseButton";
import PlayAllButton from "../../components/PlayAllButton/PlayAllButton";
import SelectionCard, { selectionDateRange } from "./components/SelectionCard";
import TitledCover from "./components/TitledCover";
import FeaturedSelection from "./components/FeaturedSelection";
import SelectionGrid from "./components/SelectionGrid";
import UpcomingSelections from "./components/UpcomingSelections";

// ─── SelectionsPage ───────────────────────────────────────────────────────────
// Same layout as the mobile app: "This week" (the newest selection, big), "Available"
// (swipeable 3 × 3 pages of covers) and "Coming soon" (a row of text cards), and a selection
// view with a large cover, dates, description and the tracks. Non-members get a 3-selection preview.
const PREVIEW_COUNT = 3;

const SelectionsPage = () => {
  const { setPlayerTrackList, setPlaylist, setTrack, isMember, setIsPricingModalOpen, user, authReady } = useAppContext();
  const { track: currentTrack } = useAppContext();
  const [searchParams] = useSearchParams();
  const selectionParam = searchParams.get("selection");

  const [selections, setSelections] = useState<Selections[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [isLoadingTracks, setIsLoadingTracks] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  // Treat unresolved subscription status as loading so membership UI never flashes
  const isLoading = dataLoading || !authReady;
  const [isFadingOut, setIsFadingOut] = useState(false);
  const navigate = useNavigate();

  // Load selections
  useEffect(() => {
    const loadSelections = async () => {
      try {
        const data = await fetchSelections();
        setSelections(data.filter((s) => s.title.toLowerCase() !== "hidden gems"));
      } catch (error) {
        console.error("Error fetching selections:", error);
      } finally {
        setDataLoading(false);
      }
    };
    loadSelections();
  }, []);

  // Load tracks for active selection
  useEffect(() => {
    if (!selectionParam) { setTracks([]); return; }
    const loadTracks = async () => {
      setIsLoadingTracks(true);
      try {
        setTracks(await fetchTracksBySelection(selectionParam));
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingTracks(false);
      }
    };
    loadTracks();
  }, [selectionParam]);

  // ─── Derived values ──────────────────────────────────────
  // The backend returns them oldest first.
  const publishedSelections = selections.filter((s) => s.is_published);
  const upcomingSelections  = selections.filter((s) => !s.is_published);
  const activeSelection     = selections.find((s) => s.id === selectionParam);
  const availableSelections = [...publishedSelections].reverse(); // newest first
  const thisWeek            = availableSelections[0];

  // ─── Handlers ────────────────────────────────────────────
  const handleSelectionClick = (selection: Selections) => {
    if (!selection.is_published) return;
    navigate(`/selections?selection=${selection.id}`);
  };

  const handleBack = () => navigate("/selections");

  const handleClose = () => {
    setIsFadingOut(true);
    setTimeout(() => navigate("/"), 1000);
  };

  const handlePlaySelection = async () => {
    if (tracks.length > 0) {
      setTrack(null);
      setPlayerTrackList(tracks);
      setPlaylist({ name: activeSelection?.title ?? '', url: `/selections?selection=${activeSelection?.id}` });
      await new Promise(resolve => setTimeout(resolve, 0));
      setTrack(tracks[0]);
    }
  };

  // "Play" on This week: load its tracks and play them from the start, without opening it.
  const handlePlayFeatured = async (selection: Selections) => {
    try {
      const selectionTracks = await fetchTracksBySelection(selection.id);
      if (!selectionTracks.length) return;
      setTrack(null);
      setPlayerTrackList(selectionTracks);
      setPlaylist({ name: selection.title, url: `/selections?selection=${selection.id}` });
      await new Promise(resolve => setTimeout(resolve, 0));
      setTrack(selectionTracks[0]);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTrackClick = (track: Track) => {
    setPlaylist({ name: activeSelection?.title ?? '', url: `/selections?selection=${activeSelection?.id}` });
    setPlayerTrackList(tracks);
    setTrack(track);
  };

  const cards = (list: Selections[]) =>
    list.map((selection) => <SelectionCard key={selection.id} selection={selection} onOpen={handleSelectionClick} />);

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className={`selectionsPage main-content ${isFadingOut ? "fade-out" : ""}`}>
      <CloseButton onClick={handleClose} />

      {isLoading && <Loader />}

      {!isLoading && selectionParam ? (
        /* ── One selection: large cover, dates, description, tracks ── */
        <div className="selectionsPage__selectionView">
          <button className="selectionsPage__back" onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>

          {activeSelection && (
            <div className="selectionsPage__hero">
              <div className="selectionsPage__hero__cover">
                <TitledCover selection={activeSelection} size={480} variant="large" />
              </div>
              <div className="selectionsPage__hero__text">
                <h1 className="selectionsPage__hero__title">{activeSelection.title}</h1>
                <p className="selectionsPage__hero__date">{selectionDateRange(activeSelection)}</p>
                {activeSelection.description && <p className="selectionsPage__hero__description">{activeSelection.description}</p>}
                {!isLoadingTracks && tracks.length > 0 && (
                  <PlayAllButton className="selectionsPage__playAll" label="Play selection" onClick={handlePlaySelection} />
                )}
              </div>
            </div>
          )}

          {isLoadingTracks ? (
            <Loader />
          ) : (
            <div className="selectionsPage__selectionView__tracks">
              {tracks.length === 0 ? (
                <p className="selectionsPage__note">No tracks found.</p>
              ) : (
                tracks.map((track, index) => (
                  <TrackItem
                    key={track.id}
                    track={track}
                    index={index}
                    onClick={() => handleTrackClick(track)}
                    isPlaying={currentTrack?.id === track.id}
                  />
                ))
              )}
            </div>
          )}
        </div>
      ) : !isLoading && !isMember ? (
        /* ── Non-members: the 3-selection preview ── */
        <div className="selectionsPage__browse">
          <div className="selectionsPage__notice">
            <p>A preview of our archive. Become a member to unlock every selection.</p>
            <button
              className="selectionsPage__subscribe-btn"
              onClick={() => user ? setIsPricingModalOpen(true) : navigate('/login')}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {user ? 'Become a member' : 'Log in'}
            </button>
          </div>
          <h2 className="selectionsPage__section">Available now</h2>
          <div className="selectionsPage__cards">{cards(publishedSelections.slice(0, PREVIEW_COUNT))}</div>
        </div>
      ) : !isLoading && (
        /* ── Members: This week · Available · Coming soon ── */
        <div className="selectionsPage__browse selectionsPage__browse--members">
          {selections.length === 0 && <p className="selectionsPage__note">No selections available yet.</p>}

          {thisWeek && (
            <section className="selectionsPage__block">
              <h2 className="selectionsPage__section">This week</h2>
              <FeaturedSelection selection={thisWeek} onOpen={handleSelectionClick} onPlay={handlePlayFeatured} />
            </section>
          )}

          {availableSelections.length > 0 && (
            <section className="selectionsPage__block">
              <div className="selectionsPage__sectionRow">
                <h2 className="selectionsPage__section">Available</h2>
                <span className="selectionsPage__count">{availableSelections.length}</span>
              </div>
              <SelectionGrid selections={availableSelections} onOpen={handleSelectionClick} />
            </section>
          )}

          {upcomingSelections.length > 0 && (
            <section className="selectionsPage__block">
              <div className="selectionsPage__sectionRow">
                <h2 className="selectionsPage__section">Coming soon</h2>
                <span className="selectionsPage__count">{upcomingSelections.length}</span>
              </div>
              <UpcomingSelections selections={upcomingSelections} />
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectionsPage;
